// sse.ts — fetch-reader SSE client for the playground chat route (spec D6/D9, M3 task-6).
// The backend (`apps/api/src/routes/playground.ts`) writes frames via hono/streaming's
// `streamSSE`, which serializes each frame as `event: <name>\ndata: <json>\n\n` and a
// heartbeat comment (`: heartbeat\n\n`) every 15s. `parseSSEChunks` is the pure parsing
// core — it only depends on an (async or sync) iterable of raw text chunks, so it's
// unit-testable against fixture arrays without touching `fetch` (see `sse.test.ts`).
// `streamSSE` wires that core to a real POST + `ReadableStream` reader.
//
// workspace-members cycle (THI-110 Task 6, fix round 1) — `streamSSE`'s `fetch` now goes
// through `lib/api.ts`'s `withWorkspaceHeader` (merged with this file's own `Content-Type`,
// not clobbered) so a chat stream started after switching into an invited workspace resolves
// against THAT workspace, not silently against the caller's default one.

import { withWorkspaceHeader } from './api';

export interface SSEFrame {
  event: string;
  data: unknown;
}

const FRAME_SEPARATOR = '\n\n';

/** Parses one `\n\n`-delimited frame. Returns `null` for comment-only frames (heartbeats —
 * every line starts with `:`, so there are no `data:` lines to yield). */
function parseFrame(rawFrame: string): SSEFrame | null {
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of rawFrame.split('\n')) {
    if (line.startsWith(':')) continue; // comment / heartbeat — ignored per SSE spec
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim());
    }
  }

  if (dataLines.length === 0) return null;

  const raw = dataLines.join('\n');
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    data = raw;
  }
  return { event, data };
}

/**
 * Pure SSE parsing core. Takes a stream of raw text chunks (as decoded from a fetch
 * response body — chunk boundaries need not align with frame boundaries) and yields
 * typed frames as complete `\n\n`-terminated frames arrive. Buffers partial frames
 * across chunk boundaries and ignores heartbeat/comment-only frames.
 */
export async function* parseSSEChunks(
  chunks: AsyncIterable<string> | Iterable<string>,
): AsyncGenerator<SSEFrame> {
  let buffer = '';

  for await (const chunk of chunks) {
    buffer += chunk;
    let separatorIndex: number;
    while ((separatorIndex = buffer.indexOf(FRAME_SEPARATOR)) !== -1) {
      const rawFrame = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + FRAME_SEPARATOR.length);
      const frame = parseFrame(rawFrame);
      if (frame) yield frame;
    }
  }

  // A trailing partial frame with no closing `\n\n` (e.g. the stream closed right after a
  // final write) is still worth parsing rather than silently dropping.
  if (buffer.trim().length > 0) {
    const frame = parseFrame(buffer);
    if (frame) yield frame;
  }
}

async function* readBodyChunks(
  reader: ReadableStreamDefaultReader<Uint8Array>,
): AsyncGenerator<string> {
  const decoder = new TextDecoder();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      yield decoder.decode(value, { stream: true });
    }
  } finally {
    reader.releaseLock();
  }
}

/** POSTs `body` as JSON to `path` (credentials included, same-origin cookies for
 * better-auth) and yields parsed SSE frames as they arrive. */
export async function* streamSSE(
  path: string,
  body: unknown,
  opts?: { signal?: AbortSignal },
): AsyncGenerator<SSEFrame> {
  const headers = withWorkspaceHeader({ 'Content-Type': 'application/json' });
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
    signal: opts?.signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`Request failed (${res.status})`);
  }

  const reader = res.body.getReader();
  yield* parseSSEChunks(readBodyChunks(reader));
}
